import { reservedUuids, sequenceTypes } from "./constants";
import { Notification } from "./Notification";
import { Instruction } from "./Instruction";
import { action, computed, makeObservable, observable } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { SequenceState } from "./constants/SequenceState";

export function Sequence(pLibrary, pApp, pSeqData) {
  Notification.call(this);

  this.mLibrary = pLibrary;
  this.mApp = pApp;
  this.mName = '';
  /** @type SequenceState */
  this.mState = SequenceState.INACTIVE;
  this.mType = sequenceTypes.sequence;
  this.mParameters = {};
  this.mInstructions = [];
  this.mSelectedInstruction = null;
  this.mIsLocked = false;
  this.mUuid = uuidv4();
  // Initialize sequence
  this.fromJson(pSeqData);

  makeObservable(this, {
    mUuid: observable,
    mInstructions: observable,
    mName: observable,
    mSelectedInstruction: observable,
    mState: observable,
    uuid: computed,
    instructions: computed,
    name: computed,
    state: computed,
    selectedInstruction: computed,
    addInstruction: action,
    removeInstruction: action,
    moveInstruction: action,
    fromJson: action,
    copyInstruction: action,
    setName: action,
    setState: action,
    setSelectedInstruction: action,
    setRobotParametersClips: action,
  });
}

Sequence.prototype = Object.create(Notification.prototype);
Sequence.prototype.constructor = Sequence;

Sequence.prototype.setDirty = function() {
  this.mApp.setDirty();
};

Sequence.prototype.setIsLocked = function(pIsLocked) { this.mIsLocked = pIsLocked; };

Sequence.prototype.getIsLocked = function() { return this.mIsLocked; };

Sequence.prototype.isInitialSequence = function() {
  // Note(alevy): here we are checking if they have the object instance
  // in case we turn our Machine Logic as immutable data, this check could fail
  const initialSequence = Object.is(this, this.mApp.mInitialSequence);
  return initialSequence;
};

Sequence.prototype.getApp = function() { return this.mApp; };

Sequence.prototype.getName = function() { return this.mName; };

Sequence.prototype.setName = function(pName) {
  if (this.mName === pName) {
    return;
  }

  const lUniqueName = this.mApp.uniqueSequenceName(pName);

  this.propagateNameChangeToApp(this.mName, lUniqueName);

  this.mName = lUniqueName;
  this.setDirty();
  this.notify('onNameChanged', this.mName);
};

Sequence.prototype.propagateNameChangeToApp = function (pOld, pNew) {
  // Propagate changes to application
  // Review (maxw):
  //   this mechanism seems awkward
  //   (why do we need to know about specific instruction and parameter names here?)
  //   (a generic notification would seem more appropriate)
  this.mApp.listSequences().forEach((pSeq)=>{
    this.mApp.getSequence(pSeq).listInstructions()
      .filter((pInst) => {return ['trigger', 'loop', 'robotLoop', 'condition', 'fsm'].includes(pInst.getCategory()); })
      .forEach((pInst) => {
        if (['trigger', 'loop', 'robotLoop'].includes(pInst.getCategory())) {
          if (pInst.getParameter('sequenceName') == pOld) {
            pInst.setParameter('sequenceName', pNew);
          }
        } else if ('fsm' == pInst.getCategory()) {
          pInst.getParameters().states.forEach((_pState)=>{
            if (_pState.actionSequenceName == pOld) {
              _pState.actionSequenceName = pNew;
            }
          });
        } else {
          const lValue = pInst.getParameter('conditions');
          lValue.forEach((lCond) => {
            if (lCond.sequenceName == pOld) {
              lCond.sequenceName = pNew;
            }
          });
          pInst.setParameter('conditions', lValue);
        }
      });
  });
};

Sequence.prototype.getType = function() { return this.mType; };

Sequence.prototype.setType = function(pType) {
  if(pType != this.mType) {
    this.mType = pType;
    switch(this.mType) {
      case sequenceTypes.loop:
        this.mParameters = { count: 1 };
        break;
      default:
        this.mParameters = {};
        break;
    }
    this.setDirty();
    this.notify('onTypeChanged', this.mType);
  }
};

Sequence.prototype.getParameter = function(pName) { return this.mParameters[pName]; };

Sequence.prototype.setParameter = function(pName, pValue) {
  if(this.mParameters.hasOwnProperty(pName)) {
    this.mParameters[pName] = pValue;
    this.setDirty();
    this.notify('onParameterChanged', pName, pValue);
  }
};

Sequence.prototype.addInstruction = function(pData, pIndex) {
  const _lInst = new Instruction(this.mApp, this, this.mLibrary.getInstructionSet(), pData);

  if (pIndex === undefined) {
    pIndex = this.mInstructions.length;
    this.mInstructions.push(_lInst);
  } else {
    this.mInstructions.splice(pIndex, 0, _lInst);
  }

  this.setDirty();
  this.notify('onInstructionAdded', _lInst, pIndex);
  return _lInst;
};

Sequence.prototype.newInstruction = function (pCategory, pName, pIndex) {
  const _lInst = {};
  const lConfigManager = this.mLibrary.getConfigManager();

  if (this.mLibrary.getInstructionSet().hasOwnProperty(pCategory)) {
    _lInst.category = pCategory;
    if (this.mLibrary.getInstructionSet()[pCategory].hasOwnProperty(pName)) {
      _lInst.name = pName;
      _lInst.parameters = {};
      this.mLibrary.getInstructionSet()[pCategory][pName].parameters.forEach(function(pParameter) {
        if (lConfigManager.numberOfAxes() == 0 && pParameter.name == 'axis'){ pParameter.default = reservedUuids.ANY; }
        // Set the value of the axis to the first one compatible with allowedAxisType
        else if (lConfigManager.numberOfAxes() > 0 && pParameter.unit == 'axis' && pParameter.allowedAxisType){
          lConfigManager.listAxes().find(function(pAxis){
            if (pParameter.allowedAxisType.includes(pAxis.getType())){
              pParameter.default = pAxis.mUUID;
              return true;
            }
          });
        }

        const robotAppsService = this.mLibrary.getRobotAppsService();
        _lInst.parameters[pParameter.name] = pParameter.default;
        if (pParameter.name === 'robotId') {
          robotAppsService.send('RobotInstruction/Sequence.prototype.newInstruction/parameter/robotId', { instruction: _lInst });

          return;
        }
        else if (pParameter.name === 'calibrationReferenceFrame') {
          robotAppsService.send('RobotInstruction/Sequence.prototype.newInstruction/parameter/calibrationReferenceFrame', { instruction: _lInst });

          return;
        }
      }, this);

      return pIndex ? this.addInstruction(_lInst, pIndex) : this.addInstruction(_lInst);
    } else {
      console.warn('Instruction category [' + pCategory + '] does not support action [' + pName + '].');
      return undefined;
    }
  } else {
    console.warn('Instruction category [' + pCategory + '] not supported.');
    return undefined;
  }
};

Sequence.prototype.removeInstruction = function(pIndex) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  if(pIndex >= 0 && pIndex < this.mInstructions.length) {
    this.mInstructions.splice(pIndex, 1);
    this.mLibrary.getConfigManager().getParentConfiguration().updateApplicationInCalibrationPart(this.mApp);
    this.setDirty();
    this.notify('onInstructionRemoved', pIndex);
  }
};

Sequence.prototype.moveInstruction = function(pFrom, pTo) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  if(pFrom >= 0 && pFrom < this.mInstructions.length && pTo >= 0 && pTo < this.mInstructions.length) {
    this.mInstructions.splice(pTo, 0, this.mInstructions.splice(pFrom, 1)[0]);

    this.mLibrary.robotAppsService.send("MachineLogic/Sequence.prototype.moveInstruction/MovedToSameSequence", { sequence: this });

    this.setDirty();
    this.notify('onInstructionMoved', pFrom, pTo);
  }
};

Sequence.prototype.getInstruction = function(pIndex) {
  if(pIndex >= 0 && pIndex < this.mInstructions.length) {
    return this.mInstructions[pIndex];
  }
};

Sequence.prototype.getInstructionIndex = function(pInstruction) {
  return this.mInstructions.indexOf(pInstruction);
};


Sequence.prototype.getSelectedInstruction = function() {
  return this.mSelectedInstruction;
};

Sequence.prototype.setSelectedInstruction = function(pInstruction, pErrorTarget, pOrigin) {
  this.mSelectedInstruction = pInstruction;
  this.notify('onInstructionSelected', pInstruction, pErrorTarget, pOrigin);
};

Sequence.prototype.numberOfInstructions = function() {
  return this.mInstructions.length;
};

/**
 * @returns {Array<Instruction>}
 */
Sequence.prototype.listInstructions = function() {
  return this.mInstructions;
};

Sequence.prototype.fromJson = function(pJson) {
  const _lThis = this;
  this.mName = pJson.name || this.mName;
  this.mType = pJson.type || this.mType;
  this.mParameters = pJson.parameters || this.mParameters;
  this.mInstructions = [];
  this.mIsLocked = pJson.isLocked || this.mIsLocked;
  this.robotParameters = pJson.robotParameters || {
    clips: []
  };
  pJson.instructions.forEach(function(pInstruction) {
    _lThis.mInstructions.push(new Instruction(_lThis.mApp, _lThis, _lThis.mLibrary.getInstructionSet(), pInstruction));
  });
};

Sequence.prototype.toJson = function() {
  const _lParams = this.mParameters;
  const _lInstructions = this.mInstructions.map(function(pInstruction) { return pInstruction.toJson(); });
  return {
    name : this.mName,
    type : this.mType,
    isLocked: this.mIsLocked,
    robotParameters: this.robotParameters,
    parameters : _lParams,
    instructions : _lInstructions
  };
};

Sequence.prototype.getObjectType = function() { return 'Sequence'; };

Sequence.prototype.hasInstructionByCategory = function(pCategory) {
  return this.mInstructions.find((instruction) => instruction.getCategory() === pCategory);
};

Sequence.prototype.copyInstruction = function(pInstructionData) {
  const instruction = new Instruction(this.mApp, this, this.mLibrary.getInstructionSet(), pInstructionData);
  this.mInstructions.push(instruction);
  this.notify('onInstructionAdded', instruction);
};

/**
 * @returns {boolean} true if the sequence has a robot simulate instruction
 */
Sequence.prototype.hasRobotSimulateInstruction = function() {
  return this.mInstructions.some((inst) => inst.mCategory === 'robot' && inst.mName === 'simulate');
};

/**
 * @param {SequenceState} state
 */
Sequence.prototype.setState = function(state) {
  return this.mState = state;
};

Sequence.prototype.setRobotParametersClips = function(newRobotClips) {
  this.robotParameters = {
    ...this.robotParameters,
    clips: newRobotClips
  };
};

Object.defineProperty(
  Sequence.prototype,
  'instructions',
  {
    get: function () {
      return this.mInstructions;
    }
  }
);

Object.defineProperty(
  Sequence.prototype,
  'name',
  {
    get: function () {
      return this.getName();
    }
  }
);

Object.defineProperty(
  Sequence.prototype,
  'state',
  {
    get: function () {
      return this.mState;
    }
  }
);

Object.defineProperty(
  Sequence.prototype,
  'selectedInstruction',
  {
    get: function () {
      return this.getSelectedInstruction();
    }
  }
);

Object.defineProperty(
  Sequence.prototype,
  'uuid',
  {
    get: function () {
      return this.mUuid;
    }
  }
);
